import {
  Checkbox,
  Flex,
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  BranchTermKind,
  BranchTermRequest,
  DeliveryMethod,
  DeliveryTerm,
  PaymentTerm,
  branchTermsApi,
} from '@texas/api/endpoints/branchesApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { HookFormCounter } from '@texas/components/shared/form/HookFormCounter';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';

export type BranchTerm = PaymentTerm | DeliveryTerm | DeliveryMethod;

const translationKeys: Record<BranchTermKind, string> = {
  deliveryMethods: 'configuration.navigation.deliveryMethod',
  deliveryTerms: 'configuration.navigation.deliveryTerms',
  paymentTerms: 'configuration.navigation.paymentTerms',
};

const emptyForm: BranchTermRequest = {
  name: '',
  identifier: '',
  displayOrder: 0,
  archived: false,
};

export function BranchTermModal({
  branchId,
  isOpen,
  kind,
  onClose,
  onSaved,
  term,
}: {
  branchId: number;
  isOpen: boolean;
  kind: BranchTermKind;
  onClose: () => void;
  onSaved: () => void;
  term: BranchTerm | null;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const {
    request: create,
    loading: creating,
    error: createError,
  } = useApiRequest(branchTermsApi.create);
  const {
    request: update,
    loading: updating,
    error: updateError,
  } = useApiRequest(branchTermsApi.update);
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<BranchTermRequest>({ defaultValues: emptyForm });
  const title = t(translationKeys[kind] as 'configuration.navigation.garp');

  useEffect(() => {
    reset(term ? { ...term } : emptyForm);
  }, [isOpen, reset, term]);

  const submit = async (request: BranchTermRequest) => {
    const response = term
      ? await update(kind, term.id, request)
      : await create(kind, branchId, request);

    if (response.hasError) return;

    toast({
      title:
        term && request.archived !== term.archived
          ? t(
              request.archived
                ? 'general.entityArchived'
                : 'general.entityRestored',
              { name: request.name },
            )
          : term
          ? t('general.updated')
          : t('general.created'),
      status: 'success',
      isClosable: true,
    });
    onSaved();
    onClose();
  };

  return (
    <Modal variant="entityDirectory" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>
          {term ? t('general.edit') : t('general.new')} {title.toLowerCase()}
        </ModalHeader>
        <form onSubmit={handleSubmit(submit)}>
          <ModalBody>
            <ErrorDetails error={createError ?? updateError} />
            <Flex direction="column" gap="4">
              <FormControl isRequired={true} isInvalid={!!errors.name}>
                <TexasFormLabel>{t('general.name')}</TexasFormLabel>
                <Input
                  variant="entityDirectory"
                  {...register('name', { required: true, maxLength: 100 })}
                />
                <HookFormCounter
                  control={control}
                  name="name"
                  maxLength={100}
                />
                <ErrorLabel text={errors.name?.message} />
              </FormControl>
              <FormControl isRequired={true} isInvalid={!!errors.identifier}>
                <TexasFormLabel>{t('configuration.identifier')}</TexasFormLabel>
                <Input
                  variant="entityDirectory"
                  {...register('identifier', {
                    required: true,
                    maxLength: 100,
                  })}
                />
                <HookFormCounter
                  control={control}
                  name="identifier"
                  maxLength={100}
                />
                <ErrorLabel text={errors.identifier?.message} />
              </FormControl>
              <FormControl isRequired={true} isInvalid={!!errors.displayOrder}>
                <TexasFormLabel>{t('branch.displayOrder')}</TexasFormLabel>
                <Input
                  type="number"
                  variant="entityDirectory"
                  {...register('displayOrder', {
                    required: true,
                    valueAsNumber: true,
                  })}
                />
                <ErrorLabel text={errors.displayOrder?.message} />
              </FormControl>
              {term && (
                <Checkbox {...register('archived')}>
                  {t('general.archived')}
                </Checkbox>
              )}
            </Flex>
          </ModalBody>
          <ModalFooter>
            <SubmitButton loading={creating || updating}>
              {t('general.save')}
            </SubmitButton>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
